/**
 * Skills Matrix Page
 * 
 * Upload a TOR (or paste text) → extract criteria → select candidates → generate scoring matrix.
 * Produces a side-by-side compliance table for team decision-making.
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Alert,
    CircularProgress,
    LinearProgress,
    Stack,
    Stepper,
    Step,
    StepLabel,
    TextField,
    Tabs,
    Tab,
    Autocomplete,
    Chip,
    IconButton,
    Tooltip
} from '@mui/material';
import {
    UploadFile as UploadFileIcon,
    PlayArrow as PlayArrowIcon,
    Refresh as RefreshIcon,
    FileDownload as FileDownloadIcon,
    Add as AddIcon
} from '@mui/icons-material';
import { extractTorForCVs, extractTorFromText } from '../services/torForCVs';
import {
    MatrixCriterion,
    CandidateOption,
    SkillsMatrixResult,
    torRequirementsToCriteria,
    generateSkillsMatrix,
    exportMatrixToExcel,
    searchCandidatesByName
} from '../services/skillsMatrix';
import CriteriaEditor from '../components/CriteriaEditor';
import SkillsMatrixTable from '../components/SkillsMatrixTable';

const STEPS = ['Define Criteria', 'Select Candidates', 'View Matrix'];

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div role="tabpanel" hidden={value !== index} {...other}>
            {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
        </div>
    );
}

const SkillsMatrixPage: React.FC = () => {
    // Stepper
    const [activeStep, setActiveStep] = useState(0);

    // Step 1: Criteria
    const [inputTab, setInputTab] = useState(0); // 0 = upload PDF, 1 = paste text
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [pastedText, setPastedText] = useState('');
    const [isExtracting, setIsExtracting] = useState(false);
    const [extractionProgress, setExtractionProgress] = useState(0);
    const [extractionError, setExtractionError] = useState<string | null>(null);
    const [criteria, setCriteria] = useState<MatrixCriterion[]>([]);
    const [torTitle, setTorTitle] = useState<string>('');

    // Step 2: Candidates
    const [candidates, setCandidates] = useState<CandidateOption[]>([]);
    const [candidateSearchInput, setCandidateSearchInput] = useState('');
    const [candidateOptions, setCandidateOptions] = useState<CandidateOption[]>([]);
    const [isSearchingCandidates, setIsSearchingCandidates] = useState(false);
    const [isAutoSuggesting, setIsAutoSuggesting] = useState(false);

    // Auto-suggest cache: stores full scoring result to avoid double LLM calls
    const [autoSuggestCache, setAutoSuggestCache] = useState<SkillsMatrixResult | null>(null);

    // Step 3: Results
    const [matrixResult, setMatrixResult] = useState<SkillsMatrixResult | null>(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [generationError, setGenerationError] = useState<string | null>(null);
    const [generationProgress, setGenerationProgress] = useState(0);

    // ─── Step 1: Extract Criteria ────────────────────────────────────────────

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            if (!file.name.toLowerCase().endsWith('.pdf')) {
                setExtractionError('Please select a PDF file');
                return;
            }
            setSelectedFile(file);
            setExtractionError(null);
        }
    };

    const handleExtractFromPDF = async () => {
        if (!selectedFile) return;
        setIsExtracting(true);
        setExtractionError(null);
        setExtractionProgress(20);

        try {
            const result = await extractTorForCVs(selectedFile);
            setExtractionProgress(70);

            if (result?.requirements) {
                const extracted = torRequirementsToCriteria(result.requirements);
                setCriteria(extracted);
                setTorTitle(result.project_title || result.requirements.project_title || selectedFile.name.replace(/\.pdf$/i, ''));
                setExtractionProgress(100);
                setActiveStep(1);
            } else {
                throw new Error('No requirements extracted from TOR');
            }
        } catch (err) {
            setExtractionError(err instanceof Error ? err.message : 'Failed to extract TOR requirements');
        } finally {
            setIsExtracting(false);
        }
    };

    const handleExtractFromText = async () => {
        if (!pastedText || pastedText.trim().length < 50) {
            setExtractionError('Please paste more text (at least a few sentences).');
            return;
        }
        setIsExtracting(true);
        setExtractionError(null);
        setExtractionProgress(20);

        try {
            const result = await extractTorFromText(pastedText);
            setExtractionProgress(70);

            if (result?.requirements) {
                const extracted = torRequirementsToCriteria(result.requirements);
                setCriteria(extracted);
                setTorTitle(result.project_title || result.requirements.project_title || 'Pasted TOR');
                setExtractionProgress(100);
                setActiveStep(1);
            } else {
                throw new Error('No requirements extracted from text');
            }
        } catch (err) {
            setExtractionError(err instanceof Error ? err.message : 'Failed to extract requirements');
        } finally {
            setIsExtracting(false);
        }
    };

    // ─── Step 2: Candidate Selection ─────────────────────────────────────────

    const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const handleCandidateSearch = useCallback((query: string) => {
        if (searchTimerRef.current) {
            clearTimeout(searchTimerRef.current);
        }
        if (!query || query.length < 2) {
            setCandidateOptions([]);
            return;
        }
        searchTimerRef.current = setTimeout(async () => {
            setIsSearchingCandidates(true);
            try {
                const results = await searchCandidatesByName(query);
                const selectedIds = new Set(candidates.map(c => c.cv_id));
                setCandidateOptions(results.filter(r => !selectedIds.has(r.cv_id)));
            } catch {
                setCandidateOptions([]);
            } finally {
                setIsSearchingCandidates(false);
            }
        }, 300);
    }, [candidates]);

    useEffect(() => {
        return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
    }, []);

    const handleAddCandidate = (_event: any, value: string | CandidateOption | null) => {
        if (value && typeof value !== 'string' && !candidates.find(c => c.cv_id === value.cv_id)) {
            setCandidates(prev => [...prev, value]);
        }
        setCandidateSearchInput('');
        setCandidateOptions([]);
    };

    const handleRemoveCandidate = (cvId: number) => {
        setCandidates(prev => prev.filter(c => c.cv_id !== cvId));
    };

    const handleAutoSuggest = async () => {
        setIsAutoSuggesting(true);
        setGenerationError(null);
        try {
            // Use the generate endpoint with empty cv_ids to auto-select
            const enabledCriteria = criteria.filter(c => c.enabled).slice(0, 40);
            const result = await generateSkillsMatrix(enabledCriteria, [], 'associate', 15);
            if (result.results.length > 0) {
                const suggested: CandidateOption[] = result.results.map(r => ({
                    cv_id: r.cv_id,
                    filename: r.filename,
                    associate_name: r.associate_name,
                    folder_name: r.folder_name,
                    source_location: r.source_location
                }));
                // Merge with existing, avoid duplicates
                const existingIds = new Set(candidates.map(c => c.cv_id));
                const newCandidates = suggested.filter(s => !existingIds.has(s.cv_id));
                setCandidates(prev => [...prev, ...newCandidates]);
                // Cache full result to avoid re-scoring on Generate Matrix
                setAutoSuggestCache(result);
            } else {
                setGenerationError('No matching candidates found. Try adding candidates manually by name.');
            }
        } catch (err) {
            setGenerationError(err instanceof Error ? err.message : 'Auto-suggest failed. Please try again.');
        } finally {
            setIsAutoSuggesting(false);
        }
    };

    // ─── Step 3: Generate Matrix ─────────────────────────────────────────────

    const handleGenerateMatrix = async () => {
        if (candidates.length === 0) return;

        setIsGenerating(true);
        setGenerationError(null);
        setGenerationProgress(10);

        try {
            const enabledCriteria = criteria.filter(c => c.enabled).slice(0, 40);
            const cvIds = candidates.map(c => c.cv_id);

            // Check if auto-suggest cache covers all selected candidates with same criteria
            let result: SkillsMatrixResult;
            const cachedCvIds = new Set(autoSuggestCache?.results.map(r => r.cv_id) || []);
            const allCached = cvIds.length > 0 && cvIds.every(id => cachedCvIds.has(id)) && autoSuggestCache;
            if (allCached && autoSuggestCache) {
                // Reuse cached scores — filter to only the selected candidates
                result = {
                    ...autoSuggestCache,
                    results: autoSuggestCache.results.filter(r => cvIds.includes(r.cv_id))
                };
                setGenerationProgress(100);
            } else {
                setGenerationProgress(30);
                result = await generateSkillsMatrix(enabledCriteria, cvIds);
                setGenerationProgress(100);
            }

            setMatrixResult(result);
            setActiveStep(2);
        } catch (err) {
            setGenerationError(err instanceof Error ? err.message : 'Failed to generate matrix');
        } finally {
            setIsGenerating(false);
        }
    };

    const handleExportExcel = async () => {
        if (!matrixResult) return;
        try {
            await exportMatrixToExcel(matrixResult, torTitle);
        } catch (err) {
            console.error('Excel export failed:', err);
        }
    };

    const handleReset = () => {
        setActiveStep(0);
        setSelectedFile(null);
        setPastedText('');
        setCriteria([]);
        setCandidates([]);
        setMatrixResult(null);
        setAutoSuggestCache(null);
        setExtractionError(null);
        setGenerationError(null);
        setTorTitle('');
    };

    // ─── Render ──────────────────────────────────────────────────────────────

    const enabledCriteriaCount = criteria.filter(c => c.enabled).length;

    return (
        <Box sx={{ maxWidth: activeStep === 2 ? 'none' : 1400, mx: 'auto', p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box>
                    <Typography variant="h5" fontWeight={700} color="white">
                        Skills Matrix
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Score CVs against TOR criteria in a side-by-side compliance matrix
                    </Typography>
                </Box>
                {activeStep > 0 && (
                    <Tooltip title="Start over">
                        <IconButton onClick={handleReset} color="inherit">
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>

            {/* Stepper */}
            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                {STEPS.map((label, index) => (
                    <Step key={label} completed={activeStep > index}>
                        <StepLabel
                            sx={{ cursor: activeStep > index ? 'pointer' : 'default' }}
                            onClick={() => { if (activeStep > index) setActiveStep(index); }}
                        >
                            {label}
                        </StepLabel>
                    </Step>
                ))}
            </Stepper>

            {/* ─── Step 1: Define Criteria ─────────────────────────────────── */}
            {activeStep === 0 && (
                <Paper sx={{ p: 3, bgcolor: 'background.paper' }}>
                    <Typography variant="h6" gutterBottom>
                        Upload TOR to Extract Criteria
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Upload a TOR PDF or paste the requirements text. The system will extract evaluation criteria that become columns in your matrix.
                    </Typography>

                    <Tabs value={inputTab} onChange={(_, v) => setInputTab(v)} sx={{ mb: 2 }}>
                        <Tab label="Upload PDF" />
                        <Tab label="Paste Text" />
                    </Tabs>

                    <TabPanel value={inputTab} index={0}>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Button
                                variant="outlined"
                                component="label"
                                startIcon={<UploadFileIcon />}
                                disabled={isExtracting}
                            >
                                {selectedFile ? selectedFile.name : 'Choose PDF'}
                                <input type="file" accept=".pdf" hidden onChange={handleFileSelect} />
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleExtractFromPDF}
                                disabled={!selectedFile || isExtracting}
                                startIcon={isExtracting ? <CircularProgress size={18} /> : <PlayArrowIcon />}
                            >
                                {isExtracting ? 'Extracting...' : 'Extract Criteria'}
                            </Button>
                        </Stack>
                    </TabPanel>

                    <TabPanel value={inputTab} index={1}>
                        <TextField
                            multiline
                            rows={8}
                            fullWidth
                            placeholder="Paste the TOR requirements section here..."
                            value={pastedText}
                            onChange={(e) => setPastedText(e.target.value)}
                            disabled={isExtracting}
                            sx={{ mb: 2 }}
                        />
                        <Button
                            variant="contained"
                            onClick={handleExtractFromText}
                            disabled={pastedText.trim().length < 50 || isExtracting}
                            startIcon={isExtracting ? <CircularProgress size={18} /> : <PlayArrowIcon />}
                        >
                            {isExtracting ? 'Extracting...' : 'Extract Criteria'}
                        </Button>
                    </TabPanel>

                    {isExtracting && (
                        <Box sx={{ mt: 2 }}>
                            <LinearProgress variant="determinate" value={extractionProgress} />
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                Analysing TOR and extracting evaluation criteria...
                            </Typography>
                        </Box>
                    )}

                    {extractionError && (
                        <Alert severity="error" sx={{ mt: 2 }}>{extractionError}</Alert>
                    )}
                </Paper>
            )}

            {/* ─── Step 1b: Edit Criteria (shown after extraction, before moving to step 2) ── */}
            {activeStep === 1 && !matrixResult && (
                <Box>
                    <Paper sx={{ p: 3, bgcolor: 'background.paper', mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Box>
                                <Typography variant="h6">
                                    Extracted Criteria ({enabledCriteriaCount} active)
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Review and edit the criteria extracted from the TOR. Toggle off criteria you don't need, or add custom ones.
                                </Typography>
                            </Box>
                            {torTitle && (
                                <Chip label={torTitle} variant="outlined" color="primary" />
                            )}
                        </Box>

                        <CriteriaEditor criteria={criteria} onChange={setCriteria} />

                        {enabledCriteriaCount > 40 && (
                            <Alert severity="warning" sx={{ mt: 2 }}>
                                {enabledCriteriaCount} criteria enabled — only the first 40 will be used. Disable some to control which are included.
                            </Alert>
                        )}
                    </Paper>

                    {/* Candidate Selection */}
                    <Paper sx={{ p: 3, bgcolor: 'background.paper', mb: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Select Candidates
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Add people to score against the criteria. Use auto-suggest to find top matches, or search by name.
                        </Typography>

                        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                            <Button
                                variant="outlined"
                                onClick={handleAutoSuggest}
                                disabled={isAutoSuggesting || enabledCriteriaCount === 0}
                                startIcon={isAutoSuggesting ? <CircularProgress size={18} /> : <AddIcon />}
                            >
                                {isAutoSuggesting ? 'Finding matches...' : 'Auto-suggest Top Matches'}
                            </Button>
                        </Stack>

                        <Autocomplete
                            freeSolo
                            options={candidateOptions}
                            getOptionLabel={(option) =>
                                typeof option === 'string' ? option : (option.associate_name || option.filename)
                            }
                            inputValue={candidateSearchInput}
                            onInputChange={(_, value, reason) => {
                                if (reason === 'reset') return;
                                setCandidateSearchInput(value);
                                handleCandidateSearch(value);
                            }}
                            onChange={handleAddCandidate}
                            loading={isSearchingCandidates}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Search by name to add candidates..."
                                    size="small"
                                    sx={{ mb: 2 }}
                                />
                            )}
                            renderOption={(props, option) => (
                                <li {...props} key={typeof option === 'string' ? option : option.cv_id}>
                                    <Box>
                                        <Typography variant="body2">
                                            {typeof option === 'string' ? option : (option.associate_name || option.filename)}
                                        </Typography>
                                        {typeof option !== 'string' && option.folder_name && (
                                            <Typography variant="caption" color="text.secondary">
                                                {option.folder_name}
                                            </Typography>
                                        )}
                                    </Box>
                                </li>
                            )}
                        />

                        {/* Selected candidates list */}
                        {candidates.length > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {candidates.map(c => (
                                    <Chip
                                        key={c.cv_id}
                                        label={c.associate_name || c.filename.replace(/\.pdf$/i, '')}
                                        onDelete={() => handleRemoveCandidate(c.cv_id)}
                                        color="primary"
                                        variant="outlined"
                                    />
                                ))}
                            </Box>
                        )}

                        {candidates.length === 0 && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                                No candidates selected yet. Use auto-suggest or search by name above.
                            </Typography>
                        )}
                    </Paper>

                    {/* Generate button */}
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Button
                            variant="contained"
                            size="large"
                            onClick={handleGenerateMatrix}
                            disabled={candidates.length === 0 || enabledCriteriaCount === 0 || isGenerating}
                            startIcon={isGenerating ? <CircularProgress size={20} /> : <PlayArrowIcon />}
                            sx={{ px: 4, py: 1.5 }}
                        >
                            {isGenerating
                                ? `Scoring ${candidates.length} candidates...`
                                : `Generate Matrix (${candidates.length} candidates × ${enabledCriteriaCount} criteria)`
                            }
                        </Button>
                    </Box>

                    {isGenerating && (
                        <Box sx={{ mt: 2 }}>
                            <LinearProgress variant="determinate" value={generationProgress} />
                            <Typography variant="caption" color="text.secondary" align="center" display="block" sx={{ mt: 0.5 }}>
                                Scoring each candidate against all criteria using AI... This may take 30–60 seconds.
                            </Typography>
                        </Box>
                    )}

                    {generationError && (
                        <Alert severity="error" sx={{ mt: 2 }}>{generationError}</Alert>
                    )}
                </Box>
            )}

            {/* ─── Step 3: View Matrix ─────────────────────────────────────── */}
            {activeStep === 2 && matrixResult && (
                <Box>
                    {/* Action bar */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Box>
                            <Typography variant="h6" color="white">
                                {torTitle || 'Skills Matrix'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {matrixResult.results.length} candidates × {matrixResult.criteria.length} criteria
                                {matrixResult.generation_time_ms > 0 && ` • Generated in ${(matrixResult.generation_time_ms / 1000).toFixed(1)}s`}
                            </Typography>
                        </Box>
                        <Stack direction="row" spacing={1}>
                            <Button
                                variant="outlined"
                                startIcon={<FileDownloadIcon />}
                                onClick={handleExportExcel}
                            >
                                Export to Excel
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={() => { setActiveStep(1); setMatrixResult(null); }}
                            >
                                Edit Criteria
                            </Button>
                        </Stack>
                    </Box>

                    {/* Summary panel */}
                    {matrixResult.summary && (
                        <Paper sx={{ p: 2, mb: 3, bgcolor: 'rgba(34, 184, 167, 0.08)', border: '1px solid rgba(34, 184, 167, 0.2)' }}>
                            <Stack direction="row" spacing={4} flexWrap="wrap">
                                {matrixResult.summary.best_fit && (
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Best Overall Fit</Typography>
                                        <Typography variant="body2" fontWeight={600} color="primary.main">
                                            {matrixResult.summary.best_fit.name} ({matrixResult.summary.best_fit.score}, {matrixResult.summary.best_fit.percentage}%)
                                        </Typography>
                                    </Box>
                                )}
                                {matrixResult.summary.hardest_criterion && (
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Hardest to Fill</Typography>
                                        <Typography variant="body2" fontWeight={600} color="warning.main">
                                            {matrixResult.summary.hardest_criterion.label} (avg {matrixResult.summary.hardest_criterion.avg_score}/3)
                                        </Typography>
                                    </Box>
                                )}
                                {matrixResult.summary.gap_notes && matrixResult.summary.gap_notes.length > 0 && (
                                    <Box>
                                        <Typography variant="caption" color="text.secondary">Gaps</Typography>
                                        {matrixResult.summary.gap_notes.map((note, i) => (
                                            <Typography key={i} variant="body2" color="error.main">
                                                {note}
                                            </Typography>
                                        ))}
                                    </Box>
                                )}
                            </Stack>
                        </Paper>
                    )}

                    {/* The matrix table */}
                    <SkillsMatrixTable matrixResult={matrixResult} />
                </Box>
            )}
        </Box>
    );
};

export default SkillsMatrixPage;
